<template>
    <!-- https://github.com/radix-ui/primitives/issues/1272 -->
    <DropdownMenu v-if="user || route.path =='/'"  v-model:open="isOpen">
        <!-- sliding out menu -->
        <div class="group relative w-fit flex items-center justify-end flex-row gap-4">
            <!-- Help Videos Button -->
             <V-Tooltip content="toggle help">
            <Button 
                @click="toggleHelpVideos" 
                variant="ghost" 
                class="select-none transition-all duration-300 rounded-full w-18 h-12 hover:bg-muted/40"
                :class="{'opacity-100 translate-x-16 sm:translate-x-12': showButtons || helpVideosButtonHovered, 'opacity-0 translate-x-20 sm:translate-x-16': !showButtons && !helpVideosButtonHovered}"
                @mouseenter="handleHelpVideosMouseEnter"
                @mouseleave="handleHelpVideosMouseLeave"
            >
                <Icon v-if="helpVideosEnabled" name="fluent:cursor-hover-28-regular" class="w-4 h-4 text-primary" />
                <Icon v-else name="fluent:cursor-hover-off-28-regular" class="w-4 h-4 text-primary" />

                
                
                
            </Button>
          </V-Tooltip>

            <!-- Darkmode Button -->
            <Button 
                @click="toggleColorMode" 
                variant="ghost" 
                class="select-none transition-all duration-300 rounded-full w-12 h-12 hover:bg-muted/40"
                :class="{'opacity-100 translate-x-12 sm:translate-x-8': showButtons || darkmodeButtonHovered, 'opacity-0 translate-x-16 sm:translate-x-12': !showButtons && !darkmodeButtonHovered}"
                @mouseenter="handleDarkmodeMouseEnter"
                @mouseleave="handleDarkmodeMouseLeave"
            >
                <Icon v-if="currentColorMode === 'dark'" name="line-md:sunny-filled-loop-to-moon-filled-alt-loop-transition" class="w-4 h-4" />
                <Icon v-else-if="currentColorMode === 'light'" name="line-md:sunny-loop" class="w-4 h-4" />
                <Icon v-else name="ic:baseline-brightness-auto" class="w-4 h-4" />
            </Button>
            
            <!-- Inbox Button -->
            <div v-if="user" 
                class="select-none transition-all duration-300 h-12 flex items-center gap-1"
                :class="{'opacity-100 translate-x-8 sm:translate-x-4': showButtons || inboxButtonHovered, 'opacity-0 translate-x-12 sm:translate-x-8': !showButtons && !inboxButtonHovered}"
                @mouseenter="handleInboxMouseEnter"
                @mouseleave="handleInboxMouseLeave"
            >
                <InboxButton />
            </div>
            
            <!-- Credits Popover -->
            <Popover v-if="user" v-model:open="creditsPopoverOpen">
                <PopoverTrigger as-child>
                    <Button 
                        variant="text" 
                        class="select-none transition-all duration-300 !w-14 h-12 flex items-center gap-1 hover:bg-muted/40 squircle-20" 
                        :class="{'opacity-100 translate-x-4 sm:translate-x-0': showButtons || creditsButtonHovered, 'opacity-0 translate-x-8 sm:translate-x-4': !showButtons && !creditsButtonHovered}"
                        @mouseenter="handleCreditsMouseEnter"
                        @mouseleave="handleCreditsMouseLeave"
                    >
                        <Icon name="streamline:nature-ecology-rice-field-sun-rise-set-field-crop-produce-farm" class="w-3 h-3" />
                        <span class="text-xs font-medium">{{ hasCredits ? formatCredits : '0' }}</span>
                    </Button>
                </PopoverTrigger>
                <PopoverContent 
                    class="w-fit p-0 rounded-3xl shadow-xxl !border-none backdrop-blur-md !bg-white/80 dark:!bg-muted" 
                    align="end" 
                    :side-offset="-50" 
                    :align-offset="-30"
                >
                    <CreditsPopover @close="creditsPopoverOpen = false" />
                </PopoverContent>
            </Popover>
            

        </div>
        <DropdownMenuTrigger class="cursor-pointer z-10 -ml-[1.5rem] sm:ml-0 flex items-center justify-end pl-20" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
            <div class="flex items-center justify-between absolute">
                
                <div v-for="i in 10" :key="i" class="masked min-w-12 min-h-12 -ml-[39px] -right-[0.3em] relative pointer-events-auto transition-all duration-300" :class="{'bg-muted': isToggled, 'bg-primary': !isToggled}"></div>

                <div v-if="plan?.product?.name" 
                    :class="[
                        'block whitespace-nowrap absolute self-center pointer-events-all transition-all duration-300',
                        isHighestPlan ? '-left-6' : (isToggled ? '-left-6' : '-left-8 opacity-0'),
                        { 'opacity-0': showTrialBadge }
                    ]" 
                    @click.stop="handleToggleClick">
                    <small
                        :class="`pr-1 px-1.5 py-1 text-xs font-semibold text-right select-none pointer-events-all ${isToggled ? 'text-primary/70' : 'text-background'}`">
                        {{ plan?.product?.name }}
                        <Icon v-if="plan?.product?.name === 'Teams'" name="lucide:users" :class="`mx-1 relative -top-[0.1em] ${isToggled ? 'text-primary/70' : 'text-background'}`" />
                        <Icon v-else name="lucide:user" :class="`mx-1 -top-0.5 ${isToggled ? 'text-primary/70' : 'text-background'}`" />
                    </small>
                </div>
                
                <div v-if="plan?.product?.name && !isHighestPlan" 
                    :class="[
                        'block whitespace-nowrap absolute self-center pointer-events-all transition-all duration-300',
                        !isToggled ? 'left-6' : 'left-8 opacity-0',
                        { 'opacity-0': showTrialBadge }
                    ]"
                    @click.stop="handleToggleClick">
                    <small
                        :class="`pr-1 px-1.5 py-1 text-xs font-semibold text-right select-none pointer-events-all ${!isToggled ? 'text-background' : 'text-primary/70'}`">
                        {{ getNextPlanName(plan?.product?.name) }}
                        <Icon v-if="getNextPlanName(plan?.product?.name) === 'Teams'" name="lucide:users" :class="`mx-1 relative -top-[0.1em] ${!isToggled ? 'text-background' : 'text-primary/70'}`" />
                        <Icon v-else name="lucide:user" :class="`mx-1 -top-0.5 ${!isToggled ? 'text-background' : 'text-primary/70'}`" />
                    </small>
                </div>
                
                
                
                <Avatar v-if="user" class="hidden border-[1.5px] border-primary pointer-events-all hover:brightness-125 transition-all duration-150">
                    <!-- <AvatarImage :src="user?.picture" :alt="user?.name" /> -->
                    <AvatarFallback>
                        {{ user?.email?.charAt(0).toUpperCase()}}
                    </AvatarFallback>
                </Avatar>
            </div>
            <!-- Wrapper for avatar and badge to ensure proper relative positioning -->
            <div class="relative">
                <div v-if="user"
                    :class="['pointer-events-all w-10 h-10 flex flex-cols justify-center items-center masked hover:brightness-125 transition-all duration-150 relative', `bg-[url('https://avatar.vercel.sh/${user.email}.png')]`, {'-translate-x-20': !isToggled}]"
                    @mouseenter="handleAvatarMouseEnter"
                    @mouseleave="handleAvatarMouseLeave">
                    <clientOnly>
                    <div 
                        class="top-0 left-0 w-full h-full" 
                        v-html="avatar(user.email || '', 48)"
                    />
                    </clientOnly>
                    <p class="absolute text-xl text-white ">
                        {{ user?.email?.charAt(0).toUpperCase()}}
                    </p>
                </div>
                
                <!-- Trial Badge - positioned over the plan name area -->
                 
                <div v-motion-pop-visible  v-if="showTrialBadge" 
                    class="absolute top-2 -left-[6.5em] bg-transparent text-primary text-xs font-regular px-2 py-1 rounded-md z-50 whitespace-nowrap pointer-events-none"
                    >
                    
                    <span v-if="trialDaysRemaining" class="text-[0.9em]">
                      <Icon name="eos-icons:hourglass" class="w-4 h-4 relative -top-[1px]" />
                        {{ trialDaysRemaining + ' days' }} 
                    </span>
                    <span v-else @click="handleUpgradeClick">
                      Upgrade
                    </span>
                  </div>
                
                </div>
            
            <!-- <Icon v-else name="lucide:menu" class="mx-3"/> -->
        </DropdownMenuTrigger>
        <!-- m-3 -mt-1  -->
        <DropdownMenuContent 
            align="end" 
            :alignOffset="-10" 
            :sideOffset="10" 
            class="w-fit min-w-[300px] max-w-[90vw] h-fit flex flex-col justify-between p-2 rounded-2xl"
        >
            <!-- <DropdownMenuItem v-if="user">
                <DropdownMenuLabel class="font-normal flex">
                    <div class="flex flex-col space-y-1">
                        <p class="text-sm font-medium leading-none">
                            {{ user?.name }}
                        </p>
                        <div class="w-44">

                            <p class="text-xs truncate w-auto text-muted-foreground">
                                {{ user.display_name || user?.email }}
                            </p>
                        </div>
                    </div> 
                </DropdownMenuLabel>
            </DropdownMenuItem> -->


            <AccountSelectTeam @close-menu="isOpen = false" />



            <DropdownMenuLabel v-for="(item, index) in menuItem.filter((item) => !item.role || role === item.role)"
                :key="index" class="font-normal flex">
                <div v-if="item.label !== 'divider'" class="flex flex-col space-y-1 w-full bg-popover z-100">
                    <DropdownMenuItem class="cursor-pointer text-foreground !bg-popover !rounded-sm hover:!bg-muted" @click="handleDropdownSelect(item.label.toLowerCase())">
                        <Icon :name="item.icon" class="mr-2 h-4 w-4" />
                        {{ item.label }}
                    </DropdownMenuItem>
                </div>
                <DropdownMenuSeparator v-else />
            </DropdownMenuLabel>


        </DropdownMenuContent>
    </DropdownMenu>
    <Button v-else variant="ghost" class="select-none">
        <NuxtLink to="/login">

        </NuxtLink>
    </Button>

</template>

<script setup lang="ts">
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import avatar from 'gradient-avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import ProTrial from '@/components/ProTrial.vue'
import CreditManager from '@/components/CreditManager.vue'
import CreditsPopover from '@/components/CreditsPopover.vue'
import InboxButton from '@/components/Inbox/InboxButton.vue'
import { useAuth } from '@/composables/useAuth'
import { useAccessControl } from '@/composables/useAccessControl'
import { useCredits } from '@/composables/useCredits'
import { useBottomDrawer } from '@/composables/useBottomDrawer'
import { usePaymentSuccessHandler } from '@/composables/usePaymentSuccessHandler'

const UserStore = useUserStore()
const { role } = UserStore
const { plan } = storeToRefs(UserStore)
const { setUser, setPlan, setTeams, fetchUserPlan } = UserStore
const uiStore = useUiStore()
const { selectedPlan, plans, helpVideosEnabled } = storeToRefs(uiStore)
const { setBottomDrawerConfig, setPlans, setSelectedPlan, toggleHelpVideos } = uiStore
const { bottomSheetOpen } = storeToRefs(uiStore)
const { open: openBottomDrawer } = useBottomDrawer()

// Function to fetch subscription data for trial info
const fetchSubscriptionData = async () => {
  try {
    
    const response = await $fetch<any>("/api/users/subscription", {
      method: "POST",
    })
    
    
    if (response && !response.error) {
      subscription.value = response
      
    }
  } catch (error) {
    console.error('Error fetching subscription data:', error)
  }
}

// Fetch the plan data once when component mounts
// This prevents multiple redundant calls when toggling plans
onMounted(async () => {
  if (user.value) {
    await fetchUserPlan()
    await fetchSubscriptionData()
    
    // Fetch credits to ensure navbar shows current amount
    await fetchCredits()
    
    // Check for payment success on mount
    checkForPaymentSuccess(openCreditsPopover)
  }
})

// Cleanup timeout on unmount
onUnmounted(() => {
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
  }
})

const colorMode = useColorMode()
const isDark = computed(() => colorMode.value === 'dark')

// Add a computed property to get the current color mode
const currentColorMode = computed(() => colorMode.preference)

// Update toggle function to cycle through dark, light, and system
const toggleColorMode = () => {
  if (colorMode.preference === 'dark') {
    colorMode.preference = 'light'
  } else if (colorMode.preference === 'light') {
    colorMode.preference = 'system'
  } else {
    colorMode.preference = 'dark'
  }
}



const showButtons = ref(false)
const creditsButtonHovered = ref(false)
const inboxButtonHovered = ref(false)
const darkmodeButtonHovered = ref(false)
const helpVideosButtonHovered = ref(false)
const creditsPopoverOpen = ref(false)
const hideTimeout = ref<NodeJS.Timeout | null>(null)
const user = useSupabaseUser()
const client = useSupabaseClient()

// Trial badge hover state
const showTrialBadge = ref(false)
const subscription = ref<any>(null)

useSquircleObserver()

const route = useRoute()
const { canCreateTeam } = useAccessControl()
const { credits, currency, hasCredits, fetchCredits, formatCredits } = useCredits()

const { signOut } = useAuth()

// Payment success handler
const { checkForPaymentSuccess, watchForPaymentSuccess } = usePaymentSuccessHandler()

// Function to open credits popover
const openCreditsPopover = () => {
  
  creditsPopoverOpen.value = true
}

// Watch for payment success in URL changes
watchForPaymentSuccess(openCreditsPopover)

const handleUpgradeClick = async () => {
  
  
  // Ensure plans data is loaded
  if (!plans.value || plans.value.length === 0) {
    try {
      
      const fetchedPlans = await $fetch("/api/plans/list", {
        method: "GET",
        credentials: 'omit'
      })
      
      setPlans(fetchedPlans?.products)
    } catch (error) {
      console.error("Error loading plans:", error)
      return
    }
  }
  
  // Set selected plan for Basic users
  const pro = plans.value?.find((plan: any) => plan.name === 'Pro')
  
  if (pro) {
    setSelectedPlan(pro)
  }
  
  // Open the bottom drawer with ProTrial component
  
  setBottomDrawerConfig({
    title: '',
    description: '',
    component: markRaw(ProTrial),
    props: {
      pro: true, // Show Pro plan for Basic users
      planDataFetched: true // Avoid redundant API calls
    }
  })
}

// Trial information computed properties
const isTrialing = computed(() => {
  return subscription.value?.status === 'trialing'
})

const trialDaysRemaining = computed(() => {
  if (!isTrialing.value || !subscription.value?.current_period_end) {
    return null
  }
  
  const endDate = new Date(subscription.value.current_period_end)
  const today = new Date()
  const diffTime = endDate.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  return diffDays > 0 ? diffDays : 0
})

// Avatar hover handlers
const handleAvatarMouseEnter = () => {
  
  // Only show trial badge if user is actually trialing
  if (isTrialing.value) {
    showTrialBadge.value = true
    
  }
}

const handleAvatarMouseLeave = () => {
  
//   showTrialBadge.value = false
}

const logout = async () => {
  const { error } = await client.auth.signOut()
    if (error) {
    console.error(error)
    return alert('Something went wrong !')
    }
  
}



const menuItem = computed(() => {
    if (user.value) {
        const items = []
        
        // Basic items always shown
        items.push(
            { label: 'Account', icon: 'lucide:user-2', role: 'admin' },
            { label: 'divider' },
            { label: 'Settings', icon: 'lucide:settings' },
            { label: 'Sign out', icon: 'lucide:log-out' }
        )

        // Only show admin if user has access
        if (canCreateTeam.value) {
            items.splice(1, 0, { label: 'Admin', icon: 'lucide:layout-dashboard', role: 'admin' })
        }

        return items
    } else {
        return [{ 
            label: 'Sign in',
            icon: 'lucide:log-in'
        }]
    }
})




async function handleDropdownSelect(key: string) {
    if (key === 'sign out') {
        await signOut()
        return
    } 
    
    const item = menuItem.value.find((item) => item.label.toLowerCase() === key)
    if (!item) return

    // Only allow navigation if user has access or it's a public route
    if (canCreateTeam.value || ['account', 'settings', 'sign in'].includes(key)) {
        // Settings navigates to /account like the personal account settings
        if (key === 'settings') {
            await navigateTo('/account')
        } else {
            await navigateTo(`/${key}`)
        }
    }
}

const isOpen = ref(false)
const isToggled = ref(true)

const isHighestPlan = computed(() => plan.value?.product?.name === 'Teams')

// Store the current plan name when toggling
const planNameWhenToggled = ref<string | null>(null)

// Watch for bottom drawer closing
watch(bottomSheetOpen, (isOpen) => {
    if (!isOpen && !isToggled.value) {
        // Only switch back if plan hasn't been upgraded
        if (plan.value?.product?.name === planNameWhenToggled.value) {
            isToggled.value = true
        }
    }
})

function handleToggleClick() {
    if (!isHighestPlan.value) {
        isToggled.value = !isToggled.value
        if (!isToggled.value) {
            planNameWhenToggled.value = plan.value?.product?.name
            const nextPlanName = getNextPlanName(plan.value?.product?.name)
            
            // Use the existing plan data instead of triggering new database calls
            setTimeout(() => {
                setBottomDrawerConfig({
                    title: '',
                    component: markRaw(ProTrial),
                    props: {
                        // If current plan is Basic, don't specify pro or team to show both
                        team: plan.value?.product?.name === 'Pro',
                        pro: false,
                        // Pass the planDataFetched flag to avoid unnecessary API calls in ProTrial
                        planDataFetched: true
                    }
                })
            }, 180)
        }
    }
}

const getNextPlanName = (currentPlan: string | undefined): string => {
    if (!currentPlan) return 'Pro' // Default to Pro if no current plan
    const planTiers = ['Basic', 'Pro', 'Teams']
    const currentIndex = planTiers.indexOf(currentPlan)
    return currentIndex < planTiers.length - 1 ? planTiers[currentIndex + 1] : planTiers[currentIndex]
}

const handleMouseLeave = () => {
  // Add a small delay before hiding to prevent flickering when moving between buttons

  hideTimeout.value = setTimeout(() => {
    showButtons.value = false
    creditsButtonHovered.value = false
    inboxButtonHovered.value = false
    darkmodeButtonHovered.value = false
    helpVideosButtonHovered.value = false
    showTrialBadge.value = false
  }, 150)
}

const handleMouseEnter = () => {
  // Only show trial badge if user is actually trialing
  if (isTrialing.value) {
    showTrialBadge.value = true
  }
  // Clear any pending hide timeout when mouse enters
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  showButtons.value = true
}

const handleCreditsMouseEnter = () => {
  // Clear any pending hide timeout
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  creditsButtonHovered.value = true
  showButtons.value = true
}

const handleCreditsMouseLeave = () => {
  creditsButtonHovered.value = false
  // Only start hide timer if not hovering over main trigger
  hideTimeout.value = setTimeout(() => {
    if (!showButtons.value) {
      creditsButtonHovered.value = false
    }
  }, 150)
}

const handleInboxMouseEnter = () => {
  // Clear any pending hide timeout
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  inboxButtonHovered.value = true
  showButtons.value = true
}

const handleInboxMouseLeave = () => {
  inboxButtonHovered.value = false
  // Only start hide timer if not hovering over main trigger
  hideTimeout.value = setTimeout(() => {
    if (!showButtons.value) {
      inboxButtonHovered.value = false
    }
  }, 150)
}

const handleDarkmodeMouseEnter = () => {
  // Clear any pending hide timeout
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  darkmodeButtonHovered.value = true
  showButtons.value = true
}

const handleDarkmodeMouseLeave = () => {
  darkmodeButtonHovered.value = false
  // Only start hide timer if not hovering over main trigger
  hideTimeout.value = setTimeout(() => {
    if (!showButtons.value) {
      darkmodeButtonHovered.value = false
    }
  }, 150)
}

const handleHelpVideosMouseEnter = () => {
  // Clear any pending hide timeout
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
    hideTimeout.value = null
  }
  helpVideosButtonHovered.value = true
  showButtons.value = true
}

const handleHelpVideosMouseLeave = () => {
  helpVideosButtonHovered.value = false
  // Only start hide timer if not hovering over main trigger
  hideTimeout.value = setTimeout(() => {
    if (!showButtons.value) {
      helpVideosButtonHovered.value = false
    }
  }, 150)
}



</script>

<style scoped>
.masked {
    mask-image: url("data:image/svg+xml,%3csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M100 0C20 0 0 20 0 100s20 100 100 100 100-20 100-100S180 0 100 0Z'/%3e%3c/svg%3e");
    mask-repeat: no-repeat;
    mask-size: 100%;
}

.masked-stretched {
    mask-image: url("data:image/svg+xml,%3csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M100 0C20 0 0 20 0 100s20 100 100 100 100-20 100-100S180 0 100 0Z'/%3e%3c/svg%3e");
    mask-repeat: no-repeat;
    mask-size: 100% 100%;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
